import { Add, Lock } from "@mui/icons-material";
import { Box } from "@mui/system";
import { App, Plugin, Route, View } from "@pimo/pimo-app-builder";
import {
  DragAndDropLayout,
  DragDropLayoutTabDefinition,
  generateTableHeaderEntriesWithSorting,
  PMButton,
  PMPopUp,
} from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import { generatePath } from "react-router-dom";
import {
  APP_CRITICALITIES,
  APP_RECORD_TYPES,
  Configuration,
  ReportingItem,
  StatusDefinition,
} from "vmd-types";
import { canUserPerformOperation } from "vmd-utils";

import { TestAppState } from "../app";
import { type FilterData } from "../components/project-filter-dialog";
// Temporarily hidden per VMD request: row/Open now navigate straight to the form instead of this modal.
// import { TrackingItemDetailsModal } from "../components/tracking-item-modal";
import {
  APP_ROUTES,
  CRITICALITY_COLORS_BY_KEY,
  FINDINGS_TAB_ID,
  RECORD_TYPE_BADGE_COLORS,
} from "../constants";
import { buildFormRoute } from "../form/build-form-route";
import { buildTrackingItemFormRoute } from "../form/build-tracking-item-form-route";
// downloadAttachment was only used by the now-hidden TrackingItemDetailsModal.
// import { downloadAttachment } from "../helpers/fetch-attachments";
import { deleteBasicItem } from "../helpers/fetch-basic-items";
import { fetchProject } from "../helpers/fetch-project";
import { createViewConfiguration } from "../helpers/fetch-view-configuration";
import { applyColumnOrderToHeadersForReportPlugin } from "../helpers/pages-helpers/report-plugin-helpers";
import { getReportDisplayTabs } from "../helpers/pages-helpers/report-tab-titles";
import {
  keepTableConfigurationInEditMode,
  restorePersistedTableConfiguration,
  saveTableConfigurationAndExitEditMode,
} from "../helpers/table-configuration-state";
import { getItemCreationDisplayName } from "../utils/get-item-creation-display-name";
import { getLockedByMessage } from "../utils/get-locked-by-message";
import { isArcherCustomId } from "../utils/is-archer-custom-id";
import { REPORT_CARDS } from "./report";
import { addCustomFieldCards } from "./report/custom-fields";
import { addTrackingItemsTable } from "./report/tracking-items-table";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ReportPluginState {}

export const REPORT_TABLE_FIELD_LOCALE_KEYS = {
  customId: "id",
  recordSource: "Record Source",
  llmSuggested: "LLM Suggested",
  name: "system",
  recordType: "systemType",
  owner: "owner",
  distributionChannel: "distributionChannel",
  lifeCycle: "lifeCycleStatus",
  criticality: "criticality",
  processSteps: "processSteps",
  applicationCapabilities: "applicationCapabilities",
  realizesDomainsAggregated: "Realizes (Domains Aggregated)",
  technicalExposure: "technicalExposure",
  dcRelevance: "DC Relevance (by OE)",
  degreeOfCompletion: "degreeOfCompletion",
  open: "open",
} as const;

export const REPORTING_SYSTEM_OVERVIEW_TABLE_FIELD_ORDER = [
  "customId",
  "recordSource",
  "llmSuggested",
  "name",
  "recordType",
  "owner",
  "distributionChannel",
  "lifeCycle",
  "criticality",
  "processSteps",
  "applicationCapabilities",
  "realizesDomainsAggregated",
  "technicalExposure",
  "dcRelevance",
  "degreeOfCompletion",
  "open",
] as const;

export const REPORT_OPEN_COLUMN_WIDTH_PX = 90;
export const REPORT_CRITICALITY_COLUMN_WIDTH_PX = 120;
export const REPORT_SYSTEM_TYPE_COLUMN_WIDTH_PX = 200;
export const REPORT_REALIZES_DOMAINS_AGGREGATED_COLUMN_WIDTH_PX = 250;
export const REPORT_MENU_COLUMN_WIDTH_PX = 36;
export const REALIZES_DOMAINS_AGGREGATED_FIELD_NAME =
  "ADOIT Realizes (Domains aggregated)";
export const EUC_FIELD_NAME = "ADOIT EUC";
export const ADOIT_ID_FIELD_NAME = "ADOIT ID";

export const findFormQuestionIdByFieldName = (
  configuration: Configuration | undefined,
  fieldName: string
) =>
  configuration?.trackingItemForms
    ?.flatMap((blueprint) => blueprint.blocks ?? [])
    .flatMap((block) => block.questions ?? [])
    .find((question) => question.fieldName?.trim() === fieldName)?.id;

export const getFormAnswerValues = (
  item: ReportingItem,
  questionId: string | undefined
): string[] => {
  if (!questionId) {
    return [];
  }

  const answer = item.formData?.[questionId];
  if (answer === undefined || answer === null) {
    return [];
  }

  return (Array.isArray(answer) ? answer : [answer])
    .flatMap((value) => String(value).split("\n"))
    .map((value) => value.trim().replace(/,$/, "").trim())
    .filter(Boolean);
};

export const getEucDisplayValue = (value: string | undefined) => {
  switch (value?.trim().toUpperCase()) {
    case "TRUE":
      return "Yes";
    case "FALSE":
      return "No";
    default:
      return undefined;
  }
};

// #2 "Add column ... for BA source": ADOIT ID and the Archer-sourced customId
// are independent identifiers on the same row, so their presence pair tells
// where the record originated. Neither present means it was added by hand.
// The id column of an ADOIT-only record holds the bare ADOIT id, so only a
// BAID-prefixed id counts as Archer's.
export const getRecordSource = (
  item: ReportingItem,
  adoitIdQuestionId: string | undefined
): "ADOIT" | "ADOIT & Archer" | "Archer" | "User input" => {
  const hasAdoitId = getFormAnswerValues(item, adoitIdQuestionId).length > 0;
  const hasArcherId = isArcherCustomId(item.basicItem?.customId);

  if (hasAdoitId && hasArcherId) {
    return "ADOIT & Archer";
  }
  if (hasAdoitId) {
    return "ADOIT";
  }
  if (hasArcherId) {
    return "Archer";
  }
  return "User input";
};

export const getSystemTypeHeaderLabel = (language: string | undefined) =>
  getLocalizedMessage(language || "en", "systemType") || "System Type";

/**
 * Resolves a single distribution-channel id to its configured label,
 * falling back to the raw id when there's no matching definition (#2303).
 * Shared by `resolveReportTableDistributionChannelCellText` (the tracking
 * items table's `"distributionChannel"` cell, which joins every resolved
 * label with `", "`) and `doesItemPassFilterCriteria`'s
 * distribution-channel filter (which lowercases each resolved label before
 * comparing). Behavior only moved, not changed.
 */
export const resolveReportTableDistributionChannelLabel = (
  channelId: string,
  distributionChannelDefinition: Configuration["distributionChannelDefinition"]
): string =>
  distributionChannelDefinition?.find((channel) => channel.id === channelId)
    ?.label ?? channelId;

export const getDcRelevanceDisplay = (
  reportingItem: ReportingItem
): {
  value: "Yes" | "No" | "Not reviewed";
  reviewed: boolean;
} =>
  reportingItem.dcRelevance
    ? { value: reportingItem.dcRelevance, reviewed: true }
    : { value: "Not reviewed", reviewed: false };

/**
 * Resolves the report table's "status" column cell props. When the
 * configuration reports status per category, the calculated semaphore
 * status (name/color) is used and the per-status definitions are omitted
 * (since the badge already carries an explicit color); otherwise the raw
 * item status is shown against the configured status definitions.
 */
export const resolveReportTableStatusCell = (
  reportingItem: ReportingItem,
  configuration: Configuration | undefined
): {
  body: string;
  color: string | undefined;
  definitions: StatusDefinition[] | undefined;
} => {
  const isCategoryLevel = configuration?.statusReportingLevel === "category";

  return {
    body: isCategoryLevel
      ? (reportingItem?._calculated?.semaphoreStatus?.name ?? "")
      : (reportingItem?.status ?? ""),
    color: isCategoryLevel
      ? reportingItem._calculated?.semaphoreStatus?.color
      : undefined,
    definitions: isCategoryLevel ? undefined : configuration?.statusDefinition,
  };
};

export const getDcSystemsFilterData = (
  filterData: FilterData | undefined
): FilterData | undefined =>
  filterData ? { ...filterData, systemTypeFilter: [] } : undefined;

const REPORT_BUSINESS_APP_EXPORT_FILTER_LABELS: Record<
  | "ownerFilter"
  | "distributionChannelFilter"
  | "lifeCycleFilter"
  | "criticalityFilter"
  | "reviewStatusFilter"
  | "realizesDomainsAggregatedFilter"
  | "eucFilter"
  | "dcRelevanceModifiedImportFilter"
  | "searchQueryFilter",
  string
> = {
  ownerFilter: "Owner",
  distributionChannelFilter: "Distribution Channel",
  lifeCycleFilter: "Life Cycle",
  criticalityFilter: "Criticality",
  reviewStatusFilter: "Review Status",
  realizesDomainsAggregatedFilter: "Realizes Domains",
  eucFilter: "EUC",
  dcRelevanceModifiedImportFilter: "Modified by ADOIT import",
  searchQueryFilter: "Search",
};

export const getActiveReportBusinessAppFilterLabels = (
  filterData: FilterData | undefined
): string[] => {
  if (!filterData) {
    return [];
  }

  return (
    Object.keys(REPORT_BUSINESS_APP_EXPORT_FILTER_LABELS) as Array<
      keyof typeof REPORT_BUSINESS_APP_EXPORT_FILTER_LABELS
    >
  )
    .filter((key) => (filterData[key] ?? []).length > 0)
    .map((key) => REPORT_BUSINESS_APP_EXPORT_FILTER_LABELS[key]);
};

/**
 * The ids (in the given, currently sorted/displayed order) of the Business
 * Application records within the current report/project that pass the
 * currently active filters, ignoring pagination (#2510). Used to drive the
 * "Download Business Apps" export for OE users on the report page.
 */
export const getReportBusinessAppExportItemIds = (
  items: ReportingItem[],
  filterData: FilterData | undefined,
  settings: TestAppState["settings"] | undefined
): string[] =>
  items
    .filter((item) => !item.isChannelHeader)
    .filter(
      (item) =>
        item.basicItem?.recordType === APP_RECORD_TYPES.BUSINESS_APPLICATION
    )
    .filter((item) => doesItemPassFilterCriteria(item, filterData, settings))
    .map((item) => item.id);

export class ReportPlugin implements Plugin<
  TestAppState,
  TestAppState,
  "id" | "viewname"
> {
  route?: Route<"id" | "viewname", TestAppState>;
  private view?: View<TestAppState, DragAndDropLayout>;
  private app?: App<TestAppState>;
  // private downloadMenuAnchor?: HTMLElement | null;

  onRegister(app: App<TestAppState>): void {
    this.app = app;
    if (!this.app) {
      return;
    }

    // Temporarily hidden per VMD request: keep implementation commented for quick restore.
    // const navigateToCurrentReport = () => {
    //   if (!this.app || !this.view) {
    //     return;
    //   }
    //
    //   const resourceLockPlugin =
    //     this.app?.getPluginByName<ResourceLockPlugin<TestAppState>>(
    //       "ResourceLockPlugin"
    //     );
    //
    //   const { currentReport } = this.app.getAppState();
    //   resourceLockPlugin?.lock(`project-update-${currentReport?.project?.id}`);
    //   this.app.patchAppState({
    //     editForm: { currentProject: currentReport?.project },
    //   });
    //   this.app.navigate(
    //     generatePath(APP_ROUTES.editReport, {
    //       id: String(currentReport?.project?.id),
    //       viewname:
    //         getLocalizedMessage(
    //           this.app?.getAppState().language || "en",
    //           "dashboard"
    //         ) || "dashboard",
    //     })
    //   );
    // };

    this.view = app.createView({
      name:
        getLocalizedMessage(
          this.app?.getAppState().language || "en",
          "reports"
        ) || "OEs",
      layout: new DragAndDropLayout(),
    });

    this.view.mapLayoutState(
      ({
        reportLayout,
        currentReport,
        settings,
        userProfile,
        showDeleteTrackingItemDialog,
        pendingDeleteTrackingItemId,
        // showTrackingItemDetailsModal,
        // selectedTrackingItem,
        language,
        reportTableConfiguration,
        lockDetails,
        isEditingReportLayout,
      }) => {
        const confirmationPopupType =
          reportTableConfiguration?.confirmationPopupType;

        const resourceLockPlugin =
          this.app?.getPluginByName<ResourceLockPlugin<TestAppState>>(
            "ResourceLockPlugin"
          );

        const currentProjectLock =
          lockDetails?.projectLocks?.[currentReport?.project?.id ?? ""];
        const isCurrentProjectEditLocked =
          currentProjectLock?.edit?.isLocked &&
          currentProjectLock?.edit?.lockedBy !== userProfile?.email;
        // Temporarily hidden per VMD request: Update Report button lock state.
        // const isCurrentProjectUpdateLocked =
        //   currentProjectLock?.update?.isLocked &&
        //   currentProjectLock?.update?.lockedBy !== userProfile?.email;
        // const selectedItemLock =
        //   lockDetails?.itemLocks?.[
        //     `item-${selectedTrackingItem?.basicItem?.id}`
        //   ];

        const baseTabs = getReportDisplayTabs(
          reportLayout,
          settings?.configuration?.itemName,
          language || "en"
        );

        const canEdit = canUserPerformOperation({
          userGroups: userProfile?.groups || [],
          lowestLevelAccessRequiredToPerformThisOperation: "admin",
          client: settings?.configuration?.client,
          tool: settings?.configuration?.tool,
        });

        const findingsTitle =
          getLocalizedMessage(language || "en", "findings") || "Findings";

        const tabs =
          canEdit && !isEditingReportLayout
            ? [
                ...baseTabs,
                { id: FINDINGS_TAB_ID, title: findingsTitle, rows: [] },
              ]
            : baseTabs;

        return {
          tabs,
          canEdit,
          displayTabs: true,
          language: language || "en",
          onTabChange: (newIndex: number) => {
            if (tabs[newIndex]?.id !== FINDINGS_TAB_ID) {
              return;
            }
            this.app?.navigate(
              generatePath(APP_ROUTES.findings, {
                id: String(currentReport?.project?.id ?? ""),
              })
            );
          },
          rightSlot: (
            <>
              <Box
                className="no-print"
                sx={{
                  display: "flex",
                  gap: 1,
                }}
              >
                {canUserPerformOperation({
                  userGroups: userProfile?.groups || [],
                  operation: "edit",
                  object: "project",
                  entityId: currentReport?.project?.id,
                  lowestLevelAccessRequiredToPerformThisOperation: "program",
                  client: settings?.configuration?.client,
                  tool: settings?.configuration?.tool,
                }) && (
                  <PMButton
                    tooltip={
                      isCurrentProjectEditLocked
                        ? getLockedByMessage(
                            "project",
                            language,
                            currentProjectLock.edit
                          )
                        : undefined
                    }
                    icon={isCurrentProjectEditLocked ? <Lock /> : undefined}
                    size="medium"
                    onClick={async () => {
                      if (!this.app || !currentReport?.project?.id) return;

                      await resourceLockPlugin?.lock(
                        `project-${currentReport.project.id}`
                      );

                      this.app.patchAppState({
                        editForm: {
                          currentProject: currentReport.project,
                        },
                      });

                      this.app.navigate(
                        generatePath(APP_ROUTES.projectDialog, {
                          id: currentReport.project.id,
                          isFromOverview: "false",
                        })
                      );
                    }}
                    text={`${getLocalizedMessage(language, "editProject", {
                      projectName:
                        settings?.configuration?.projectName ?? "Project",
                    })} `}
                    variant={
                      isCurrentProjectEditLocked ? "disabled" : "contained"
                    }
                  />
                )}
                {canUserPerformOperation({
                  userGroups: userProfile?.groups || [],
                  operation: "edit",
                  object: "item",
                  entityId: currentReport?.project?.id,
                  client: settings?.configuration?.client,
                  tool: settings?.configuration?.tool,
                }) && (
                  <PMButton
                    size="medium"
                    variant="outlined"
                    icon={<Add />}
                    text={`${
                      getLocalizedMessage(language, "addNew") || "Add New"
                    } ${getItemCreationDisplayName(
                      settings?.configuration?.itemName
                    )}`}
                    onClick={() => {
                      this.app?.navigate(
                        generatePath(APP_ROUTES.trackingItemDialog, {
                          id: currentReport?.project?.id ?? "",
                          "tracking-item-id": "new-tracking-item",
                          viewname: "dashboard",
                        })
                      );
                    }}
                  />
                )}
                {/*
                  Temporarily hidden per VMD request: keep Update Report action commented
                  until a better solution is implemented.

                  {canUserPerformOperation({
                    userGroups: userProfile?.groups || [],
                    entityId: currentReport?.project?.id,
                    operation: "report",
                    object: "project",
                    client: settings?.configuration?.client,
                    tool: settings?.configuration?.tool,
                  }) &&
                    settings?.configuration?.isReportingEnabled && (
                      <PMButton
                        size="medium"
                        tooltip={
                          isCurrentProjectUpdateLocked
                            ? getLockedByMessage(
                                "project-update",
                                language,
                                currentProjectLock.update
                              )
                            : undefined
                        }
                        icon={isCurrentProjectUpdateLocked ? <Lock /> : undefined}
                        onClick={navigateToCurrentReport}
                        text={
                          getLocalizedMessage(language, "updateReport") ||
                          "Update Report"
                        }
                        variant={
                          isCurrentProjectUpdateLocked ? "disabled" : "report"
                        }
                      />
                    )}
                */}
                {/* Hide download button until a proper download template is implemented */}
                {/* <IconButton */}
                {/*   onClick={(event) => { */}
                {/*     this.downloadMenuAnchor = event.currentTarget; */}
                {/*     this.app?.patchAppState({}); */}
                {/*   }} */}
                {/*   sx={{ ml: 1 }} */}
                {/* > */}
                {/*   <FileDownloadOutlined /> */}
                {/* </IconButton> */}
              </Box>
              {/* Hide download button until a proper download template is implemented */}
              {/* <Menu */}
              {/*   anchorEl={this.downloadMenuAnchor} */}
              {/*   open={Boolean(this.downloadMenuAnchor)} */}
              {/*   onClose={() => { */}
              {/*     this.downloadMenuAnchor = null; */}
              {/*     this.app?.patchAppState({}); */}
              {/*   }} */}
              {/* > */}
              {/*   <MenuItem */}
              {/*     onClick={() => { */}
              {/*       setTimeout(() => { */}
              {/*         window.print(); */}
              {/*       }, 1000); */}
              {/*       this.downloadMenuAnchor = null; */}
              {/*       this.app?.patchAppState({}); */}
              {/*     }} */}
              {/*   > */}
              {/*     Download PDF */}
              {/*   </MenuItem> */}
              {/*   <MenuItem */}
              {/*     onClick={() => { */}
              {/*       const projectId = currentReport?.project?.id; */}
              {/*       if (projectId) { */}
              {/*         downloadProjectExcelExport(projectId); */}
              {/*       } */}
              {/*       this.downloadMenuAnchor = null; */}
              {/*       this.app?.patchAppState({}); */}
              {/*     }} */}
              {/*     sx={{ */}
              {/*       color: "text.primary", */}
              {/*       "&:hover": { */}
              {/*         backgroundColor: "action.hover", */}
              {/*       }, */}
              {/*     }} */}
              {/*   > */}
              {/*     Download Excel */}
              {/*   </MenuItem> */}
              {/* </Menu> */}
              {/* Inline confirmation dialog so it renders within the view rightSlot */}
              <PMPopUp
                open={!!showDeleteTrackingItemDialog}
                title={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.title.confirmDelete"
                  ) || "Confirm Deletion"
                }
                description={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.message.genericConfirm",
                    { itemName: `${settings?.configuration?.itemName}` }
                  ) ||
                  `Are you sure you want to delete this ${settings?.configuration?.itemName}? This action cannot be undone.`
                }
                onClose={() => {
                  this.app?.patchAppState({
                    showDeleteTrackingItemDialog: false,
                    pendingDeleteTrackingItemId: undefined,
                  });
                }}
                maxWidth="sm"
                cancelButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.cancel"
                  ) || "Cancel"
                }
                cancelButtonFireEvent={() => {
                  this.app?.patchAppState({
                    showDeleteTrackingItemDialog: false,
                    pendingDeleteTrackingItemId: undefined,
                  });
                }}
                cancelButtonColor="white"
                confirmationButtonColor="error"
                confirmationButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.delete"
                  ) || "Delete"
                }
                confirmationButtonFireEvent={async () => {
                  const basicItemId = pendingDeleteTrackingItemId;
                  if (basicItemId) {
                    try {
                      await deleteBasicItem(basicItemId);
                      if (currentReport?.project?.id) {
                        const updatedProject = await fetchProject({
                          id: currentReport.project.id,
                        });
                        this.app?.patchAppState({
                          currentReport: updatedProject,
                        });
                      }
                    } catch {
                      // ignore errors for now
                    }
                  }

                  this.app?.patchAppState({
                    showDeleteTrackingItemDialog: false,
                    pendingDeleteTrackingItemId: undefined,
                  });
                }}
              />
              {/* Confirmation dialog for saving layout changes across report pages */}
              <PMPopUp
                open={
                  !!this.app?.getAppState().showConfirmSaveReportLayoutDialog
                }
                title={
                  getLocalizedMessage(
                    language || "en",
                    "saveLayoutDialog.title"
                  ) || "Save layout changes"
                }
                description={
                  getLocalizedMessage(
                    language || "en",
                    "saveLayoutDialog.message"
                  ) ||
                  "When saving the layout, all other report pages will be adapted as well. Do you want to save these changes for all reports?"
                }
                onClose={() => {
                  this.app?.patchAppState({
                    showConfirmSaveReportLayoutDialog: false,
                  });
                }}
                maxWidth="sm"
                cancelButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.cancel"
                  ) || "Cancel"
                }
                cancelButtonFireEvent={() => {
                  this.app?.patchAppState({
                    showConfirmSaveReportLayoutDialog: false,
                  });
                }}
                cancelButtonColor="white"
                confirmationButtonColor="blue"
                confirmationButtonText={
                  getLocalizedMessage(language || "en", "dialog.button.save") ||
                  "Save"
                }
                confirmationButtonFireEvent={async () => {
                  if (!this.app) return;

                  const { pendingSaveReportLayout, reportLayout } =
                    this.app.getAppState();

                  // clear the dialog flag
                  this.app.patchAppState({
                    showConfirmSaveReportLayoutDialog: false,
                  });

                  if (pendingSaveReportLayout) {
                    try {
                      await createViewConfiguration({
                        tabs: pendingSaveReportLayout.tabs,
                        canEdit: reportLayout?.canEdit ?? true,
                        displayTabs: pendingSaveReportLayout.displayTabs,
                        type: pendingSaveReportLayout.type ?? "project-common",
                      });
                      // show success snackbar
                      this.app.patchAppState({
                        snackbar: {
                          message:
                            "Layout saved. Changes have been applied to all report pages.",
                          severity: "success",
                          open: true,
                          type: "snackbar",
                        },
                      });
                    } catch {
                      // show error snackbar
                      this.app.patchAppState({
                        snackbar: {
                          message:
                            "Failed to save layout changes. Please try again.",
                          severity: "error",
                          open: true,
                          type: "snackbar",
                        },
                      });
                    }
                  } else {
                    // if not confirmed, revert the in-memory layout to the last saved layout
                    if (reportLayout && reportLayout.tabs) {
                      this.app.patchAppState({ reportLayout });
                    }
                  }

                  // remove pending data
                  this.app.patchAppState({
                    pendingSaveReportLayout: undefined,
                  });
                }}
              />
              {/* TableConfigurrator Confirmation Popup */}
              <PMPopUp
                open={!!confirmationPopupType}
                title={
                  confirmationPopupType === "save"
                    ? getLocalizedMessage(
                        language || "en",
                        "dialog.title.saveChanges"
                      ) || "Save table changes"
                    : getLocalizedMessage(
                        language || "en",
                        "dialog.title.unsavedChanges"
                      ) || "Unsaved Changes"
                }
                description={
                  confirmationPopupType === "save"
                    ? getLocalizedMessage(
                        language || "en",
                        "dialog.message.saveTableConfiguration"
                      ) ||
                      "When saving table configuration, the tables of all other report pages will be adapted as well. Do you want to save these changes for all report pages?"
                    : getLocalizedMessage(
                        language || "en",
                        "dialog.message.abortTableConfiguration"
                      ) ||
                      "You have made changes that have not been saved. If you close the table configurator, these changes will be lost."
                }
                onClose={async () => {
                  if (!this.app) {
                    return;
                  }

                  keepTableConfigurationInEditMode(
                    this.app,
                    "reportTableConfiguration"
                  );
                }}
                maxWidth="sm"
                cancelButtonText={
                  getLocalizedMessage(
                    language || "en",
                    "dialog.button.cancel"
                  ) || "Cancel"
                }
                cancelButtonFireEvent={async () => {
                  if (!this.app) {
                    return;
                  }

                  keepTableConfigurationInEditMode(
                    this.app,
                    "reportTableConfiguration"
                  );
                }}
                cancelButtonColor="white"
                confirmationButtonColor="blue"
                confirmationButtonText={
                  confirmationPopupType === "save"
                    ? getLocalizedMessage(
                        language || "en",
                        "dialog.button.save"
                      ) || "Save"
                    : getLocalizedMessage(
                        language || "en",
                        "dialog.button.continue"
                      ) || "Continue"
                }
                confirmationButtonFireEvent={async () => {
                  if (!this.app) return;

                  if (confirmationPopupType === "save") {
                    await saveTableConfigurationAndExitEditMode({
                      app: this.app,
                      configKey: "reportTableConfiguration",
                      columnOrderKey: "reportPluginColumnOrder",
                      componentId: "trackingItemsTableComponent",
                      applyColumnOrder:
                        applyColumnOrderToHeadersForReportPlugin,
                    });

                    return;
                  }

                  await restorePersistedTableConfiguration({
                    app: this.app,
                    configKey: "reportTableConfiguration",
                    columnOrderKey: "reportPluginColumnOrder",
                    componentId: "trackingItemsTableComponent",
                  });
                }}
              />
              {/*
                Temporarily hidden per VMD request: row click and Open button now navigate
                straight to the tracking item form instead of opening this details modal.

                <TrackingItemDetailsModal
                  open={!!showTrackingItemDetailsModal}
                  onClose={() => {
                    this.app?.patchAppState({
                      showTrackingItemDetailsModal: false,
                      selectedTrackingItem: undefined,
                    });
                  }}
                  itemLockDetail={
                    selectedItemLock?.isLocked &&
                    selectedItemLock.lockedBy !== userProfile?.email
                      ? selectedItemLock
                      : undefined
                  }
                  reportingLockDetail={currentProjectLock?.update}
                  selectedTrackingItem={selectedTrackingItem}
                  configuration={settings?.configuration}
                  currentReport={currentReport}
                  attachments={selectedTrackingItem?.attachments}
                  onDownloadAttachment={downloadAttachment}
                  onEditMeasure={(trackingItem) => {
                    // Close the modal first before navigating to the edit form
                    this.app?.patchAppState({
                      showTrackingItemDetailsModal: false,
                      selectedTrackingItem: undefined,
                    });

                    this.app?.patchAppState({
                      editForm: {
                        currentTrackingItem: trackingItem.basicItem
                          ? {
                              ...trackingItem.basicItem,
                              distributionChannel:
                                trackingItem.distributionChannel,
                            }
                          : undefined,
                      },
                    });

                    this.app?.navigate(
                      generatePath(APP_ROUTES.trackingItemDialog, {
                        id: currentReport?.project?.id ?? "",
                        "tracking-item-id": trackingItem.basicItem?.id ?? "",
                        viewname: "dashboard",
                      })
                    );
                  }}
                  onUpdateMeasure={() => {
                    // Close the modal
                    this.app?.patchAppState({
                      showTrackingItemDetailsModal: false,
                      selectedTrackingItem: undefined,
                    });

                    // Navigate to update report screen with the specific tab ID
                    this.app?.patchAppState({
                      editForm: { currentProject: currentReport?.project },
                    });

                    const basePath = generatePath(APP_ROUTES.editReport, {
                      id: String(currentReport?.project?.id),
                      viewname: "dashboard",
                    });

                    const tabId = selectedTrackingItem?.basicItem?.id?.toString();
                    const pathWithTab = tabId
                      ? `${basePath}?tabId=${tabId}`
                      : basePath;

                    this.app?.navigate(pathWithTab);
                  }}
                />
              */}
            </>
          ),
        };
      }
    );

    const view = this.view;
    this.route = app.createRoute<"id" | "viewname">({
      path: "/reports/:id/:viewname",
      view: view,
    });

    buildFormRoute(app, this.route);
    buildTrackingItemFormRoute(app, this.route);

    this.view.on("updateLayout", async ({ payload }) => {
      if (!payload || !this.app) return;

      const { reportLayout } = this.app.getAppState();

      const currentDisplayTabs = reportLayout?.displayTabs ?? true;

      const { tabs: incomingTabs } = payload as {
        tabs: DragDropLayoutTabDefinition[];
      };

      // Strip the admin-only Findings stub tab before persisting: it's derived
      // (appended in mapLayoutState) and must never be saved as part of the
      // user-editable report layout, or it would duplicate on next load and
      // skew the next-tab-id allocation (Math.max(...ids) + 1).
      const tabs = incomingTabs.filter((tab) => tab.id !== FINDINGS_TAB_ID);

      // patch the in-memory layout immediately so the UI reflects the drag-drop action
      this.app.patchAppState({
        reportLayout: {
          displayTabs: currentDisplayTabs,
          tabs,
        },
      });

      // open a confirmation dialog that is rendered in the view rightSlot
      // store the pending tabs in app state so the dialog handler can access them
      this.app.patchAppState({
        pendingSaveReportLayout: {
          tabs,
          displayTabs: currentDisplayTabs,
          type: "project-common",
        },
        showConfirmSaveReportLayoutDialog: true,
      });
    });
    this.view?.on("startLayoutEdit", () => {
      this.app?.patchAppState({
        isSidebarOpen: false,
        isEditingReportLayout: true,
      });
    });

    this.view?.on("stopLayoutEdit", () => {
      this.app?.patchAppState({
        isSidebarOpen: true,
        isEditingReportLayout: false,
      });
    });
    this.view.buildDynamicallyBasedOnAppState(
      (appState) => ({
        configuration: appState.settings?.configuration,
        project: appState.currentReport?.project,
      }),

      (state, view) => {
        // static components are built here
        this.addComponentsToLayout(true, state.configuration);
        // dynamic components are built here
        addCustomFieldCards(view, state);
      }
    );
  }

  async addComponentsToLayout(
    includeTrackingItemsTable = true,
    configuration: Configuration | undefined = this.app?.getAppState().settings
      ?.configuration
  ) {
    if (!this.app || !this.view) {
      return;
    }

    const language = this.app.getAppState().language || "en";

    for (const addCard of REPORT_CARDS) {
      addCard(this.view, this.app);
    }

    /**
     * Add the tracking items table component to the view.
     */
    if (includeTrackingItemsTable) {
      addTrackingItemsTable(this.view, this.app, language, configuration);
    }
  }
}

export const doesItemPassFilterCriteria = (
  item: ReportingItem,
  filterData: FilterData | undefined,
  settings: TestAppState["settings"] | undefined
): boolean => {
  if (!filterData) {
    return true;
  }

  const selectedOwners = (filterData.ownerFilter ?? [])
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  const selectedDistributionChannels = (
    filterData.distributionChannelFilter ?? []
  )
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  const selectedLifeCycles = (filterData.lifeCycleFilter ?? [])
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  const selectedCriticalities = (filterData.criticalityFilter ?? [])
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  const selectedReviewStatuses = (filterData.reviewStatusFilter ?? [])
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  const selectedRealizesDomainsAggregated = (
    filterData.realizesDomainsAggregatedFilter ?? []
  )
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  const selectedEucValues = (filterData.eucFilter ?? [])
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  const selectedModifiedImportValues = (
    filterData.dcRelevanceModifiedImportFilter ?? []
  )
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);

  const ownerValue = (item.basicItem?.owner ?? "").trim().toLowerCase();
  if (selectedOwners.length > 0 && !selectedOwners.includes(ownerValue)) {
    return false;
  }

  const distributionChannelLabels = (item.distributionChannel ?? []).map(
    (channelId) =>
      resolveReportTableDistributionChannelLabel(
        channelId,
        settings?.configuration?.distributionChannelDefinition
      )
        .trim()
        .toLowerCase()
  );
  if (
    selectedDistributionChannels.length > 0 &&
    !distributionChannelLabels.some((label) =>
      selectedDistributionChannels.includes(label)
    )
  ) {
    return false;
  }

  const lifeCycleValue = (item.basicItem?.lifeCycle ?? "").trim().toLowerCase();
  if (
    selectedLifeCycles.length > 0 &&
    !selectedLifeCycles.includes(lifeCycleValue)
  ) {
    return false;
  }

  const criticalityValue = (item.basicItem?.criticality ?? "")
    .trim()
    .toLowerCase();
  if (
    selectedCriticalities.length > 0 &&
    !selectedCriticalities.includes(criticalityValue)
  ) {
    return false;
  }

  const reviewStatusValue = (item.degreeOfCompletion ?? "")
    .trim()
    .toLowerCase();
  if (
    selectedReviewStatuses.length > 0 &&
    !selectedReviewStatuses.includes(reviewStatusValue)
  ) {
    return false;
  }

  if (selectedRealizesDomainsAggregated.length > 0) {
    const realizesDomainsAggregatedValues = getFormAnswerValues(
      item,
      findFormQuestionIdByFieldName(
        settings?.configuration,
        REALIZES_DOMAINS_AGGREGATED_FIELD_NAME
      )
    ).map((value) => value.toLowerCase());

    if (
      !realizesDomainsAggregatedValues.some((value) =>
        selectedRealizesDomainsAggregated.includes(value)
      )
    ) {
      return false;
    }
  }

  if (selectedEucValues.length > 0) {
    const eucValues = getFormAnswerValues(
      item,
      findFormQuestionIdByFieldName(settings?.configuration, EUC_FIELD_NAME)
    )
      .map((value) => getEucDisplayValue(value)?.toLowerCase())
      .filter((value): value is string => Boolean(value));

    if (!eucValues.some((value) => selectedEucValues.includes(value))) {
      return false;
    }
  }

  if (selectedModifiedImportValues.length > 0) {
    const modifiedByImport = item.dcRelevanceModifiedImportedAt ? "yes" : "no";
    if (!selectedModifiedImportValues.includes(modifiedByImport)) {
      return false;
    }
  }

  const searchQuery = (filterData.searchQueryFilter ?? []).join(" ").trim();

  const normalizedQuery = searchQuery.toLowerCase();
  if (normalizedQuery.length > 0) {
    const name = (item.basicItem?.name ?? "").toLowerCase();
    const customId = (item.basicItem?.customId?.toString() ?? "").toLowerCase();
    const recordType = (item.basicItem?.recordType ?? "").toLowerCase();
    const owner = (item.basicItem?.owner ?? "").toLowerCase();
    const criticality = (item.basicItem?.criticality ?? "").toLowerCase();
    const processSteps = (item.basicItem?.processSteps ?? [])
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const applicationCapabilities = (
      item.basicItem?.applicationCapabilities ?? []
    )
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const technicalExposure = (item.basicItem?.technicalExposure ?? [])
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const matches =
      name.includes(normalizedQuery) ||
      customId.includes(normalizedQuery) ||
      recordType.includes(normalizedQuery) ||
      owner.includes(normalizedQuery) ||
      criticality.includes(normalizedQuery) ||
      processSteps.includes(normalizedQuery) ||
      applicationCapabilities.includes(normalizedQuery) ||
      technicalExposure.includes(normalizedQuery);

    if (!matches) {
      return false;
    }
  }

  return true;
};

/**
 * Distribution-channel cell text for the tracking items table (#2303):
 * resolves an item's distribution-channel ids to their configured labels
 * (falling back to the raw id when a definition doesn't match) and joins
 * them with ", ", or returns "n/a" when the item has no distribution
 * channels at all. Pulled out of `addTrackingItemsTable`'s `mapState`
 * row-mapping callback (`baseCellByFieldName`'s `"distributionChannel"`
 * entry) so it can be tested as a pure function, same pattern as
 * `mergeReportTableHeaderEntries`/`deriveReportTableColumnVisibility`/
 * `resolveReportTableColumnOrder`/`buildReportTableCustomFieldValueMaps`.
 * Behavior only moved, not changed.
 */
export const resolveReportTableDistributionChannelCellText = (
  channelIds: string[] | undefined,
  distributionChannelDefinition: Configuration["distributionChannelDefinition"]
): string => {
  const ids = channelIds ?? [];
  if (ids.length === 0) return "n/a";

  return ids
    .map((channelId) =>
      resolveReportTableDistributionChannelLabel(
        channelId,
        distributionChannelDefinition
      )
    )
    .join(", ");
};

/**
 * Column-visibility and sx-styling derivation for the tracking items table
 * (#2303): applies per-field sticky positioning, width overrides, and the
 * `headerVisibilityByField` hide-list on top of the header entries produced
 * by `generateTableHeaderEntriesWithSorting`. Pulled out of
 * `addTrackingItemsTable`'s `mapState` callback so it can be tested as a
 * pure function without mounting the table.
 */
export function deriveReportTableColumnVisibility<TableData>(
  tableHeaderEntries: ReturnType<
    typeof generateTableHeaderEntriesWithSorting<TableData>
  >,
  {
    hasVisibleOpenColumn,
    openColumnWidthPx,
    degreeColumnWidthPx,
  }: {
    hasVisibleOpenColumn: boolean;
    openColumnWidthPx: number;
    degreeColumnWidthPx: number;
  }
): typeof tableHeaderEntries {
  const headerVisibilityByField: Record<string, boolean> = {
    customId: true,
    recordSource: true,
    name: true,
    recordType: true,
    owner: true,
    distributionChannel: true,
    lifeCycle: true,
    criticality: true,
    processSteps: true,
    applicationCapabilities: true,
    realizesDomainsAggregated: true,
    technicalExposure: true,
    open: true,
  };

  return tableHeaderEntries.map((entry) => {
    const isIdColumn = entry.fieldName === "customId";
    const isArrayFieldColumn = [
      "processSteps",
      "applicationCapabilities",
      "technicalExposure",
    ].includes(String(entry.fieldName));
    const isSystemTypeColumn = String(entry.fieldName) === "recordType";
    const isRealizesDomainsAggregatedColumn =
      String(entry.fieldName) === "realizesDomainsAggregated";
    const isDegreeOfCompletionColumn =
      String(entry.fieldName) === "degreeOfCompletion";
    const isCriticalityColumn = String(entry.fieldName) === "criticality";
    const isOpenColumn = String(entry.fieldName) === "open";
    const isDcRelevanceColumn = String(entry.fieldName) === "dcRelevance";
    const sxObject =
      entry.sx && typeof entry.sx === "object" && !Array.isArray(entry.sx)
        ? (entry.sx as Record<string, unknown>)
        : undefined;

    const normalizedSx = isIdColumn
      ? {
          ...(sxObject ?? {}),
          position: "sticky" as const,
          left: 0,
          zIndex: 4,
          backgroundColor: "background.paper",
        }
      : sxObject
        ? (() => {
            const sxWithoutSticky = { ...sxObject };
            delete sxWithoutSticky.position;
            delete sxWithoutSticky.left;
            delete sxWithoutSticky.right;
            return sxWithoutSticky;
          })()
        : entry.sx;

    const normalizedSxWithFlexibleApplicationCapabilities = isSystemTypeColumn
      ? {
          ...(normalizedSx &&
          typeof normalizedSx === "object" &&
          !Array.isArray(normalizedSx)
            ? (normalizedSx as Record<string, unknown>)
            : {}),
          width: `${REPORT_SYSTEM_TYPE_COLUMN_WIDTH_PX}px`,
          minWidth: `${REPORT_SYSTEM_TYPE_COLUMN_WIDTH_PX}px`,
        }
      : isRealizesDomainsAggregatedColumn
        ? {
            ...(normalizedSx &&
            typeof normalizedSx === "object" &&
            !Array.isArray(normalizedSx)
              ? (normalizedSx as Record<string, unknown>)
              : {}),
            width: `${REPORT_REALIZES_DOMAINS_AGGREGATED_COLUMN_WIDTH_PX}px`,
            maxWidth: `${REPORT_REALIZES_DOMAINS_AGGREGATED_COLUMN_WIDTH_PX}px`,
          }
        : isArrayFieldColumn
          ? {
              ...(normalizedSx &&
              typeof normalizedSx === "object" &&
              !Array.isArray(normalizedSx)
                ? (normalizedSx as Record<string, unknown>)
                : {}),
              maxWidth: "200px",
              width: "200px",
            }
          : isCriticalityColumn
            ? {
                ...(normalizedSx &&
                typeof normalizedSx === "object" &&
                !Array.isArray(normalizedSx)
                  ? (normalizedSx as Record<string, unknown>)
                  : {}),
                width: `${REPORT_CRITICALITY_COLUMN_WIDTH_PX}px`,
                minWidth: `${REPORT_CRITICALITY_COLUMN_WIDTH_PX}px`,
              }
            : isDcRelevanceColumn
              ? {
                  ...(normalizedSx &&
                  typeof normalizedSx === "object" &&
                  !Array.isArray(normalizedSx)
                    ? (normalizedSx as Record<string, unknown>)
                    : {}),
                  // Fixed width so the sort/filter icon in this
                  // header never gets cut off/covered by the
                  // adjacent sticky "Review Status" column.
                  width: "170px",
                }
              : isDegreeOfCompletionColumn
                ? {
                    ...(normalizedSx &&
                    typeof normalizedSx === "object" &&
                    !Array.isArray(normalizedSx)
                      ? (normalizedSx as Record<string, unknown>)
                      : {}),
                    width: `${degreeColumnWidthPx}px`,
                    position: "sticky" as const,
                    right: `${hasVisibleOpenColumn ? openColumnWidthPx : 0}px`,
                    zIndex: 4,
                    backgroundColor: "background.paper",
                  }
                : isOpenColumn
                  ? {
                      ...(normalizedSx &&
                      typeof normalizedSx === "object" &&
                      !Array.isArray(normalizedSx)
                        ? (normalizedSx as Record<string, unknown>)
                        : {}),
                      width: `${openColumnWidthPx}px`,
                      position: "sticky" as const,
                      right: 0,
                      zIndex: 5,
                      backgroundColor: "background.paper",
                    }
                  : normalizedSx;

    return {
      ...entry,
      label: isOpenColumn ? "" : entry.label,
      sx: normalizedSxWithFlexibleApplicationCapabilities,
      isHidden:
        entry.isHidden ||
        headerVisibilityByField[String(entry.fieldName)] === false,
    };
  });
}

/**
 * Criticality-badge color lookup for the tracking items table (#2303):
 * resolves an item's criticality label (one of `APP_CRITICALITIES`'
 * values) to its badge color, falling back to the generic badge color
 * when the label doesn't match any known criticality (including
 * `undefined`/legacy labels). Pulled out of `addTrackingItemsTable`'s
 * `mapState` row-mapping callback (`baseCellByFieldName`'s `"criticality"`
 * entry) so it can be tested as a pure function, same pattern as
 * `mergeReportTableHeaderEntries`/`deriveReportTableColumnVisibility`/
 * `resolveReportTableColumnOrder`/`buildReportTableCustomFieldValueMaps`.
 * Behavior only moved, not changed.
 */
export const resolveReportTableCriticalityBadgeColor = (
  criticalityLabel: string | undefined
): string => {
  const criticalityKey = Object.entries(APP_CRITICALITIES).find(
    ([, label]) => label === criticalityLabel
  )?.[0] as keyof typeof APP_CRITICALITIES | undefined;

  return criticalityKey
    ? CRITICALITY_COLORS_BY_KEY[criticalityKey]
    : RECORD_TYPE_BADGE_COLORS.fallback;
};

/**
 * `dropdownFieldOne`-badge color lookup for the tracking items table
 * (#2303): resolves an item's `dropdownFieldOne` value to the color
 * configured for it in `Configuration.dropdownFieldOneDefinition`, falling
 * back to the caller-supplied default color when the value is unset, the
 * configuration is missing, or the value doesn't match any configured
 * option. Pulled out of `addTrackingItemsTable`'s `mapState` row-mapping
 * callback (`baseCellByFieldName`'s `"dropdownFieldOne"` entry). Behavior
 * only moved, not changed.
 */
export const resolveReportTableDropdownFieldOneColor = (
  dropdownFieldOneValue: string | undefined,
  configuration: Configuration | undefined,
  fallbackColor: string
): string =>
  configuration?.dropdownFieldOneDefinition?.find(
    (cfg) => cfg.name === dropdownFieldOneValue
  )?.color ?? fallbackColor;

/**
 * Resolves the tracking items table's effective column order (#2303):
 * checks whether a persisted `reportPluginColumnOrder` is still a valid
 * permutation of the table's current column indices (same length, no
 * duplicates, no out-of-range/missing indices — the header set can change
 * across sessions as custom fields are added/removed), and falls back to
 * the natural (identity) order otherwise. `needsSeeding` tells the caller
 * whether to persist that fallback so drag/reorder state is restored after
 * a refresh. Pulled out of `addTrackingItemsTable`'s `mapState` callback so
 * it can be tested as a pure function, same pattern as
 * `mergeReportTableHeaderEntries`/`deriveReportTableColumnVisibility`.
 */
export const resolveReportTableColumnOrder = (
  columnCount: number,
  savedColumnOrder: number[] | undefined
): {
  defaultColumnOrder: number[];
  effectiveColumnOrder: number[];
  needsSeeding: boolean;
} => {
  const defaultColumnOrder = Array.from(
    { length: columnCount },
    (_, index) => index
  );

  const isValidColumnOrder =
    !!savedColumnOrder && savedColumnOrder.length === columnCount
      ? (() => {
          const expected = new Set(defaultColumnOrder);
          const actual = new Set(savedColumnOrder);

          return (
            actual.size === expected.size &&
            [...expected].every((index) => actual.has(index))
          );
        })()
      : false;

  return {
    defaultColumnOrder,
    effectiveColumnOrder: isValidColumnOrder
      ? savedColumnOrder!
      : defaultColumnOrder,
    needsSeeding: !isValidColumnOrder,
  };
};

/**
 * Builds per-item custom-field value lookups for the tracking items table
 * (#2303): folds an item's `customFields` into two maps — keyed by the
 * table's custom-field-name convention (e.g. `customField-my-label`, as
 * produced by the caller-supplied `toCustomColumnFieldName`) and by the
 * field's raw label (trimmed/lowercased) — so a column can resolve its
 * value whether the persisted header entry uses the field-name key or (for
 * legacy/renamed fields) still carries the original label. When a field
 * name or label repeats across an item's custom fields, the first
 * "meaningful" (non-null, non-blank) value wins and a later blank value
 * doesn't clobber it, but a later meaningful value still overwrites an
 * earlier blank one. Pulled out of `addTrackingItemsTable`'s `mapState`
 * row-mapping callback so it can be tested as a pure function, same
 * pattern as
 * `mergeReportTableHeaderEntries`/`deriveReportTableColumnVisibility`/`resolveReportTableColumnOrder`.
 */
export const buildReportTableCustomFieldValueMaps = (
  itemCustomFields: {
    value?: unknown;
    customFieldDefinition?: {
      label?: string;
    };
  }[],
  toCustomColumnFieldName: (label?: string) => string
): {
  customFieldValueByFieldName: Map<string, unknown>;
  customFieldValueByLabel: Map<string, unknown>;
} => {
  const customFieldValueByFieldName = new Map<string, unknown>();
  const customFieldValueByLabel = new Map<string, unknown>();

  for (const customFieldValue of itemCustomFields) {
    const fieldName = toCustomColumnFieldName(
      customFieldValue.customFieldDefinition?.label
    );
    const labelKey = String(customFieldValue.customFieldDefinition?.label ?? "")
      .trim()
      .toLowerCase();

    const nextValue = customFieldValue.value;
    const currentValue = customFieldValueByFieldName.get(fieldName);
    const currentLabelValue = customFieldValueByLabel.get(labelKey);

    const hasMeaningfulValue =
      nextValue != null && String(nextValue).trim() !== "";
    const currentHasMeaningfulValue =
      currentValue != null && String(currentValue).trim() !== "";
    const labelHasMeaningfulValue =
      currentLabelValue != null && String(currentLabelValue).trim() !== "";

    if (!currentHasMeaningfulValue || hasMeaningfulValue) {
      customFieldValueByFieldName.set(fieldName, nextValue);
    }

    if (labelKey && (!labelHasMeaningfulValue || hasMeaningfulValue)) {
      customFieldValueByLabel.set(labelKey, nextValue);
    }
  }

  return { customFieldValueByFieldName, customFieldValueByLabel };
};
